import React, { useState } from 'react';
import { InsightElement, ReportStatus } from '../../shared/types/reports';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

interface ReportCanvasProps {
  layout: InsightElement[];
  status: ReportStatus;
  onChange: (newLayout: InsightElement[]) => void;
  editable: boolean;
  metrics?: any;
  minimal?: boolean;
}

// Enhanced table component with real campaign data
const DocumentTable: React.FC<{ dataKey: string; config: any; metrics: any }> = ({ dataKey, config, metrics }) => {
  // Generate real data based on dataKey and available metrics
  const getTableData = () => {
    if (dataKey === 'meta_spend' && metrics?.meta?.dailySpend) {
      return Object.entries(metrics.meta.dailySpend).map(([date, value]) => ({ 
        date, 
        spend: value,
        impressions: metrics.meta?.dailyImpressions?.[date] || Math.round((value as number) * 1000),
        clicks: metrics.meta?.dailyClicks?.[date] || Math.round((value as number) * 50)
      }));
    }
    
    if (dataKey === 'campaign_performance' && metrics?.meta) {
      return [
        { metric: 'Total Spend', value: `$${metrics.meta.spend?.toLocaleString() || '0'}` },
        { metric: 'Impressions', value: metrics.meta.impressions?.toLocaleString() || '0' },
        { metric: 'Clicks', value: metrics.meta.clicks?.toLocaleString() || '0' },
        { metric: 'CTR', value: `${metrics.meta.ctr?.toFixed(2) || '0'}%` },
        { metric: 'CPC', value: `$${((metrics.meta.spend || 0) / (metrics.meta.clicks || 1)).toFixed(2)}` },
        { metric: 'CPM', value: `$${((metrics.meta.spend || 0) / ((metrics.meta.impressions || 1) / 1000)).toFixed(2)}` }
      ];
    }

    // Default sample data
    return [
      { date: '2025-09-28', spend: 100, impressions: 10000, clicks: 50 },
      { date: '2025-09-29', spend: 120, impressions: 12000, clicks: 60 },
      { date: '2025-09-30', spend: 95, impressions: 9500, clicks: 45 }
    ];
  };

  const data = getTableData();
  const isPerformanceTable = dataKey === 'campaign_performance';

  const table = useReactTable({
    data,
    columns: isPerformanceTable ? [
      { accessorKey: 'metric', header: 'Metric', cell: info => info.getValue() },
      { accessorKey: 'value', header: 'Value', cell: info => info.getValue() },
    ] : [
      { accessorKey: 'date', header: 'Date', cell: info => new Date(info.getValue() as string).toLocaleDateString() },
      { accessorKey: 'spend', header: 'Spend', cell: info => `$${info.getValue()}` },
      { accessorKey: 'impressions', header: 'Impressions', cell: info => (info.getValue() as number).toLocaleString() },
      { accessorKey: 'clicks', header: 'Clicks', cell: info => (info.getValue() as number).toLocaleString() },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="my-6">
      <h3 className="text-lg font-medium mb-3">{config.title || 'Campaign Data'}</h3>
      <table className="w-full text-sm">
        <thead className="border-b">
          <tr>
            {table.getHeaderGroups().map(hg => 
              hg.headers.map(h => (
                <th key={h.id} className="text-left py-2 font-medium text-gray-700">
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id} className="border-b">
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className="py-2">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Clean text component with proper state management
const DocumentText: React.FC<{ config: any; onUpdate: (text: string) => void; editable: boolean }> = ({ config, onUpdate, editable }) => {
  const [text, setText] = useState(config.text || 'Click to edit text...');
  const [editing, setEditing] = useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  // Update local state when config changes (but avoid infinite loops)
  React.useEffect(() => {
    if (config.text && config.text !== text && !editing) {
      setText(config.text);
    }
  }, [config.text, editing]);

  if (!editable) {
    return <p className="my-4">{text}</p>;
  }

  const handleTextChange = (newText: string) => {
    setText(newText);
    
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce the update to prevent rapid state changes
    timeoutRef.current = setTimeout(() => {
      onUpdate(newText);
    }, 300);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return editing ? (
    <textarea
      value={text}
      onChange={(e) => handleTextChange(e.target.value)}
      onBlur={() => setEditing(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
      className="w-full my-4 p-2 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
      rows={3}
      autoFocus
    />
  ) : (
    <p 
      className="my-4 cursor-text hover:bg-gray-50 p-2 -mx-2 rounded"
      onClick={() => setEditing(true)}
    >
      {text}
    </p>
  );
};

// Enhanced KPI component with real metrics
const DocumentKPI: React.FC<{ config: any; metrics: any }> = ({ config, metrics }) => {
  const getKPIValue = () => {
    const dataKey = config.dataKey || 'total_spend';
    
    switch (dataKey) {
      case 'total_spend':
        return {
          value: metrics?.meta?.spend || 0,
          label: 'Total Spend',
          format: 'currency'
        };
      case 'impressions':
        return {
          value: metrics?.meta?.impressions || 0,
          label: 'Impressions',
          format: 'number'
        };
      case 'clicks':
        return {
          value: metrics?.meta?.clicks || 0,
          label: 'Clicks',
          format: 'number'
        };
      case 'ctr':
        return {
          value: metrics?.meta?.ctr || 0,
          label: 'CTR',
          format: 'percentage'
        };
      case 'cpc':
        return {
          value: metrics?.meta?.spend && metrics?.meta?.clicks ? 
            metrics.meta.spend / metrics.meta.clicks : 0,
          label: 'CPC',
          format: 'currency'
        };
      default:
        return {
          value: config.value || 1234,
          label: config.label || 'Metric',
          format: 'number'
        };
    }
  };

  const kpi = getKPIValue();
  
  const formatValue = (value: number, format: string) => {
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'number':
        return value.toLocaleString();
      default:
        return value.toString();
    }
  };
  
  return (
    <div className="my-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
      <div className="text-center">
        <div className="text-4xl font-light text-blue-900 mb-2">
          {formatValue(kpi.value, kpi.format)}
        </div>
        <div className="text-sm text-blue-700 font-medium">{kpi.label}</div>
        {config.description && (
          <div className="text-xs text-blue-600 mt-1">{config.description}</div>
        )}
      </div>
    </div>
  );
};

export const ReportCanvas: React.FC<ReportCanvasProps> = ({ 
  layout, 
  status, 
  onChange, 
  editable = true,
  metrics,
  minimal = false
}) => {
  const updateElement = React.useCallback((index: number, updated: Partial<InsightElement>) => {
    const newLayout = layout.map((el, i) => i === index ? { ...el, ...updated } : el);
    onChange(newLayout);
  }, [layout, onChange]);

  const deleteElement = React.useCallback((index: number) => {
    onChange(layout.filter((_, i) => i !== index));
  }, [layout, onChange]);

  return (
    <div className={minimal ? '' : 'p-4'}>
      {layout.map((el, idx) => (
        <div key={el.config?.id || idx} className="group relative">
          {/* Delete button on hover */}
          {editable && (
            <button
              onClick={() => deleteElement(idx)}
              className="absolute -left-8 top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {/* Render element based on type */}
          {el.type === 'text' && (
            <DocumentText 
              config={el.config} 
              onUpdate={(text) => updateElement(idx, { config: { ...el.config, text } })} 
              editable={editable}
            />
          )}
          {el.type === 'table' && (
            <DocumentTable dataKey={el.dataKey || 'sample'} config={el.config} metrics={metrics} />
          )}
          {el.type === 'kpi' && (
            <DocumentKPI config={el.config} metrics={metrics} />
          )}
          {el.type === 'chart' && (
            <div className="my-6 p-8 bg-gray-100 rounded text-center text-gray-500">
              Chart: {el.config?.title || 'Visualization'}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};